import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Dealership from '../../../../types/dealership/Dealership';
import BatteryStatus from '../../../../types/physical-sample/BatteryStatus';
import PhysicalSample from '../../../../types/physical-sample/PhysicalSample';
import PhysicalSampleDiscovery from '../../../../types/physical-sample/PhysicalSampleDiscovery';
import PhysicalSampleState from '../../../../types/physical-sample/PhysicalSampleState';
import PhysicalSampleWriteDTO from '../../../../types/physical-sample/PhysicalSampleWriteDTO';
import Sample from '../../../../types/sample/Sample';
import Page from '../../../../types/shared/Page';
import { Role } from '../../../../types/shared/Role';
import SelectOption from '../../../../types/shared/SelectOption';
import Studio from '../../../../types/studio/Studio';
import { IsProducerDirective } from '../../directives/is-producer.directive';
import { EnumLabelPipe } from '../../pipes/enum-label.pipe';
import { AuthService } from '../../services/auth.service';
import { CookieService } from '../../services/cookie.service';
import { DealershipService } from '../../services/dealership.service';
import { ExportService } from '../../services/export.service';
import { PhysicalSampleService } from '../../services/physical-sample.service';
import { SampleService } from '../../services/sample.service';
import { StudioService } from '../../services/studio.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    CreatePhysicalSampleDialogComponent
} from '../shared/physical-samples/create-physical-sample-dialog/create-physical-sample-dialog.component';
import {
    FilterPhysicalSamplesDialogComponent
} from '../shared/physical-samples/filter-physical-samples-dialog/filter-physical-samples-dialog.component';
import {
    UpdatePhysicalSampleDialogComponent
} from '../shared/physical-samples/update-physical-sample-dialog/update-physical-sample-dialog.component';

@Component({
    selector: 'app-physical-samples',
    templateUrl: './physical-samples.component.html',
    styleUrls: [ './physical-samples.component.scss' ],
    imports: [
        FormsModule,
        RouterLink,
        PageHeaderComponent,
        IftaLabelModule,
        IconField,
        InputIcon,
        InputText,
        Button,
        CreatePhysicalSampleDialogComponent,
        UpdatePhysicalSampleDialogComponent,
        Tag,
        Tooltip,
        TableModule,
        CheckboxModule,
        Select,
        DatePipe,
        IsProducerDirective,
        FilterPhysicalSamplesDialogComponent,
        EnumLabelPipe
    ]
})
export class PhysicalSamplesComponent implements OnInit, OnDestroy {
    public search = '';
    public physicalSamples: PhysicalSample[] = [];
    public physicalSampleDiscoveries: PhysicalSampleDiscovery[] = [];
    public loadingDiscoveries = false;
    public discoveriesHidden = false;
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateDialog = false;
    public displayUpdateDialog = false;
    public selectedPhysicalSample: PhysicalSampleWriteDTO =
        new PhysicalSampleWriteDTO({});

    private searchSubject = new Subject<string>();

    private readonly discoveriesHiddenCookieName = 'discoveries_hidden';

    // Filter
    public filterDialogVisible = false;
    public stateOptions: SelectOption[] = [
        { label: 'Online', value: PhysicalSampleState.ONLINE },
        { label: 'Offline', value: PhysicalSampleState.OFFLINE },
        { label: 'Unknown', value: PhysicalSampleState.UNKNOWN },
    ];
    public firmwareVersionOptions: SelectOption[] = [];
    public ownerDealershipOptions: SelectOption[] = [];
    public studioOptions: SelectOption[] = [];
    public sampleOptions: Sample[] = [];
    public selectedStates: PhysicalSampleState[] = [];
    public selectedFirmwareVersions: string[] = [];
    public selectedOwnerDealershipIds: number[] = [];
    public selectedStudioIds: number[] = [];
    public selectedSampleIds: number[] = [];
    public selectedBatteryStatuses: BatteryStatus[] = [];
    public sortField: string = '';
    public sortOrder: number = 1;

    private intervalId: any;
    private polling = false;

    constructor(
        private physicalSampleService: PhysicalSampleService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private exportService: ExportService,
        private studioService: StudioService,
        private dealershipService: DealershipService,
        private sampleService: SampleService,
        private authService: AuthService,
        private cookieService: CookieService
    ) {
        this.discoveriesHidden = this.cookieService.get(this.discoveriesHiddenCookieName) === 'true';
    }

    /** Remembers the panel's visibility in a cookie across page reloads. */
    public toggleDiscoveriesVisibility(): void {
        this.discoveriesHidden = !this.discoveriesHidden;
        this.cookieService.set(this.discoveriesHiddenCookieName, String(this.discoveriesHidden), 365);
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadPhysicalSamples();
        this.loadPhysicalSampleDiscoveries();

        this.intervalId = setInterval(() => {
            if (this.polling) {
                return;
            }
            if (!this.displayCreateDialog && !this.displayUpdateDialog) {
                this.polling = true;
                this.loadPhysicalSamples(false).finally(() => {
                    this.polling = false;
                });
                // Discoveries aren't auto-refreshed: it would clear the user's in-progress row selections.
            }
        }, 10000); // every 10 seconds refresh the physical samples data

        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadPhysicalSamples();
        });

        this.loadFilterOptions().then();
    }

    /**
     * Upon destruction of the component.
     */
    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId); // cleanup
        }
    }

    /**
     * Reacts to the page change event from the table.
     *
     * @param event The pagination event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.sortField = event.sortField || '';
        this.sortOrder = event.sortOrder || 1;
        this.loadPhysicalSamples();
    }

    /**
     * Handles the change of the search input.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the edit dialog for a physical sample.
     */
    public editPhysicalSample(physicalSample: PhysicalSample): void {
        this.selectedPhysicalSample = new PhysicalSampleWriteDTO(physicalSample);
        this.selectedPhysicalSample.sampleId = physicalSample?.sample?.id;
        this.selectedPhysicalSample.ownerDealershipId =
            physicalSample?.ownerDealership?.id;
        this.selectedPhysicalSample.studioId = physicalSample?.studio?.id;
        this.displayUpdateDialog = true;
    }

    /**
     * Deletes a physical sample after confirming with the user.
     *
     * @param physicalSample The physical sample to delete.
     */
    public deletePhysicalSample(physicalSample: PhysicalSample): void {
        this.confirmationService.confirm({
            message:
                'Are you sure you want to delete the physical sample with MAC address ' +
                physicalSample.macAddress +
                '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.physicalSampleService
                    .deletePhysicalSample(physicalSample.id)
                    .then(() => {
                        this.loadPhysicalSamples();
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Physical sample deleted',
                            life: 3000,
                        });
                    })
                    .catch((error: any) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message,
                            life: 3000,
                        });
                    });
            },
        });
    }

    /**
     * Opens the dialog to create a new physical sample.
     */
    public openCreateDialog(): void {
        this.displayCreateDialog = true;
    }

    public loadPhysicalSampleDiscoveries(showError = true): void {
        if (!this.authService.hasRole(Role.SAMPLE_STUDIO_PRODUCER)) {
            return;
        }
        this.loadingDiscoveries = true;
        this.physicalSampleService.getPhysicalSampleDiscoveries()
            .then(discoveries => this.physicalSampleDiscoveries = discoveries)
            .catch((error: any) => {
                if (showError) {
                    this.messageService.add({
                        severity: 'error', summary: 'Error', detail: error.message, life: 3000
                    });
                }
            })
            .finally(() => this.loadingDiscoveries = false);
    }

    public confirmPhysicalSampleDiscovery(discovery: PhysicalSampleDiscovery): void {
        if (!discovery.selectedSampleId) {
            return;
        }
        discovery.confirming = true;
        this.physicalSampleService.confirmPhysicalSampleDiscovery(
            discovery.id, discovery.selectedSampleId, discovery.selectedStudioId)
            .then(() => {
                this.physicalSampleDiscoveries = this.physicalSampleDiscoveries
                    .filter(item => item.id !== discovery.id);
                this.loadPhysicalSamples();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Physical sample confirmed',
                    detail: `${ discovery.macAddress } is now available in SampleStudio.`,
                    life: 3000
                });
            })
            .catch((error: any) => this.messageService.add({
                severity: 'error', summary: 'Error', detail: error.message, life: 3000
            }))
            .finally(() => discovery.confirming = false);
    }

    public ignorePhysicalSampleDiscovery(discovery: PhysicalSampleDiscovery): void {
        this.confirmationService.confirm({
            header: 'Ignore discovered sample',
            message: `Ignore ${ discovery.macAddress }? It will no longer appear in the discovery inbox.`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.physicalSampleService.ignorePhysicalSampleDiscovery(discovery.id)
                    .then(() => this.physicalSampleDiscoveries = this.physicalSampleDiscoveries
                        .filter(item => item.id !== discovery.id))
                    .catch((error: any) => this.messageService.add({
                        severity: 'error', summary: 'Error', detail: error.message, life: 3000
                    }));
            }
        });
    }

    /**
     * Reloads the physical samples.
     */
    public reloadPhysicalSamples(): void {
        this.loadPhysicalSamples();
    }

    /**
     * Exports the physical samples to a file.
     */
    public exportPhysicalSamples(): void {
        this.exportService
            .exportPhysicalSamples()
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Physical samples exported successfully',
                    life: 3000,
                });
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            });
    }

    /**
     * Handles the application of filters from the filter dialog.
     *
     * @param filters The filters applied by the user.
     */
    public onFiltersApplied(filters: {
        state: PhysicalSampleState[];
        firmwareVersions: string[];
        ownerDealershipIds: number[];
        studioIds: number[];
        sampleIds: number[];
        batteryStatuses: BatteryStatus[];
    }): void {
        this.selectedStates = filters.state ?? [];
        this.selectedFirmwareVersions = filters.firmwareVersions ?? [];
        this.selectedOwnerDealershipIds = filters.ownerDealershipIds ?? [];
        this.selectedStudioIds = filters.studioIds ?? [];
        this.selectedSampleIds = filters.sampleIds ?? [];
        this.selectedBatteryStatuses = filters.batteryStatuses ?? [];
        this.currentPage = 0;
        this.loadPhysicalSamples();
    }

    /**
     * Displays the filter dialog.
     */
    public displayFilterDialog() {
        this.filterDialogVisible = true;
    }

    /**
     * Checks if any filters are currently active.
     *
     * @return True if any filter is active, false otherwise.
     */
    get hasActiveFilters() {
        return this.selectedStates.length > 0 ||
            this.selectedFirmwareVersions.length > 0 ||
            this.selectedOwnerDealershipIds.length > 0 ||
            this.selectedStudioIds.length > 0 ||
            this.selectedSampleIds.length > 0 ||
            this.selectedBatteryStatuses.length > 0;
    }

    /**
     * Loads the physical samples from the service.
     *
     * @param showLoading Whether to show the loading indicator.
     */
    private loadPhysicalSamples(showLoading: boolean = true): Promise<void> {
        this.loading = showLoading;

        const size = this.rows;
        const search = this.search || '';

        return this.physicalSampleService
            .getPhysicalSamples(
                search,
                this.currentPage,
                size,
                this.selectedStates,
                this.selectedFirmwareVersions,
                this.selectedOwnerDealershipIds,
                this.selectedStudioIds,
                this.selectedSampleIds,
                this.selectedBatteryStatuses,
                this.sortField,
                this.sortOrder
            )
            .then((result: Page<PhysicalSample>) => {
                this.physicalSamples = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Loads options for filters.
     */
    private async loadFilterOptions(): Promise<void> {
        try {
            // Studios
            const studios: Studio[] = await this.studioService.getAllStudios();
            this.studioOptions = studios
                .filter(s => s?.id != null)
                .map(s => new SelectOption({ label: s.name, value: s.id }));

            // Dealerships
            const dealerships: Dealership[] = await this.dealershipService.getAllDealerships();
            this.ownerDealershipOptions = dealerships
                .filter(d => d?.id != null)
                .map(d => new SelectOption({ label: d.name, value: d.id }));

            // Firmware Versions
            const versions: string[] = await this.physicalSampleService.getDistinctFirmwareVersions();
            this.firmwareVersionOptions = versions
                .filter(v => !!v)
                .map(v => new SelectOption({ label: v, value: v }));

            // Sample Options
            this.sampleOptions = await this.sampleService.getAllSamples();
        } catch (e: any) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load filter options',
                life: 3000,
            });
        }
    }

    /**
     * Generates a summary text of the active filters.
     *
     * @return A string summarizing the active filters.
     */
    get activeFiltersText(): string {
        const parts: string[] = [];

        if (this.selectedStates.length) {
            parts.push(`State: ${ this.selectedStates.join(', ') }`);
        }
        if (this.selectedFirmwareVersions.length) {
            parts.push(`FW: ${ this.selectedFirmwareVersions.join(', ') }`);
        }
        if (this.selectedOwnerDealershipIds.length) {
            parts.push(`Dealerships: ${ this.selectedOwnerDealershipIds.length }`);
        }
        if (this.selectedStudioIds.length) {
            parts.push(`Studios: ${ this.selectedStudioIds.length }`);
        }
        if (this.selectedSampleIds.length) {
            parts.push(`Samples: ${ this.selectedSampleIds.length }`);
        }
        if (this.selectedBatteryStatuses.length) {
            parts.push(`Battery: ${ this.selectedBatteryStatuses.join(', ') }`);
        }

        return parts.length > 0 ? parts.join(' • ') : '';
    }

}
